import {
  COMBINE_COPY,
  GpuContext,
  GpuImage,
  clearPaintMap,
  combine,
  createPaintMap,
  paintStroke,
  textureFromExternalImage,
  type BrushOptions,
  type ExternalImageSource,
  type PaintTarget,
  type StrokePoint,
} from '@design-ai/gpu-core';
import { OnnxModel, type ModelEntry } from '@design-ai/inference';
import { MattingStage, estimateBackground, refineMatte } from './matting.js';
import { UpscaleStage, type UpscaleProgress } from './upscale.js';
import {
  DEFAULT_MATTE_SETTINGS,
  DEFAULT_UPSCALE_SETTINGS,
  type MatteSettings,
  type UpscaleSettings,
} from './settings.js';
import {
  exportArtwork,
  exportForRedesign,
  type ExportOptions,
  type ExportResult,
  type RedesignAssets,
} from './export.js';

export type WorkbenchStage = 'empty' | 'loaded' | 'matted' | 'upscaled';

export interface WorkbenchState {
  stage: WorkbenchStage;
  sourceWidth: number;
  sourceHeight: number;
  outputWidth: number;
  outputHeight: number;
  hasMatte: boolean;
  hasUpscale: boolean;
}

/**
 * Holds the document and drives the stages.
 *
 * The split that matters: `runMatting` and `runUpscale` are the expensive,
 * explicit steps, while `refine` is pure GPU post-processing that re-runs on
 * every slider move. Manual brush strokes go into their own override texture, so
 * they survive re-refinement - the user can clean an edge by hand and then keep
 * tuning parameters without losing the work.
 */
export class Workbench {
  #source: GpuImage | null = null;
  #rawMatte: GpuImage | null = null;
  #paint: GpuImage | null = null;
  #cutout: GpuImage | null = null;
  #upscaled: GpuImage | null = null;

  #matting: MattingStage | null = null;
  #mattingModel: OnnxModel | null = null;
  #upscaleStage: UpscaleStage | null = null;
  #upscaleModel: OnnxModel | null = null;

  matteSettings: MatteSettings = { ...DEFAULT_MATTE_SETTINGS };
  upscaleSettings: UpscaleSettings = { ...DEFAULT_UPSCALE_SETTINGS };

  constructor(readonly ctx: GpuContext) {}

  get state(): WorkbenchState {
    const output = this.displayImage;
    return {
      stage: this.#upscaled ? 'upscaled' : this.#cutout ? 'matted' : this.#source ? 'loaded' : 'empty',
      sourceWidth: this.#source?.width ?? 0,
      sourceHeight: this.#source?.height ?? 0,
      outputWidth: output?.width ?? 0,
      outputHeight: output?.height ?? 0,
      hasMatte: this.#rawMatte !== null,
      hasUpscale: this.#upscaled !== null,
    };
  }

  /** What the canvas should show: the newest artefact available. */
  get displayImage(): GpuImage | null {
    return this.#upscaled ?? this.#cutout ?? this.#source;
  }

  get source(): GpuImage | null {
    return this.#source;
  }

  get cutout(): GpuImage | null {
    return this.#cutout;
  }

  get upscaled(): GpuImage | null {
    return this.#upscaled;
  }

  /**
   * Replaces the document with a pasted or dropped image.
   *
   * The source is kept in `rgba16float`: decontamination divides by alpha, and
   * doing that in 8-bit visibly posterises the semi-transparent band along every
   * edge.
   */
  loadSource(source: ExternalImageSource, width: number, height: number): void {
    this.#disposeDocument();

    const uploaded = textureFromExternalImage(this.ctx.device, source, width, height, 'rgba8unorm');
    const working = GpuImage.create(this.ctx.device, width, height, 'rgba16float', 'source');
    combine(this.ctx, uploaded, uploaded, working, COMBINE_COPY);
    this.ctx.flush();
    uploaded.destroy();

    this.#source = working;
    this.#paint = createPaintMap(this.ctx, width, height);
    this.ctx.flush();
  }

  /** Installs the matting network. The previous one is released. */
  async setMattingModel(model: OnnxModel, entry: ModelEntry): Promise<void> {
    this.#matting?.dispose();
    if (this.#mattingModel && this.#mattingModel !== model) await this.#mattingModel.dispose();
    this.#mattingModel = model;
    this.#matting = new MattingStage(this.ctx, model, entry.io);
  }

  async setUpscaleModel(model: OnnxModel, entry: ModelEntry): Promise<void> {
    this.#upscaleStage?.dispose();
    if (this.#upscaleModel && this.#upscaleModel !== model) await this.#upscaleModel.dispose();
    this.#upscaleModel = model;
    this.#upscaleStage = new UpscaleStage(this.ctx, model, entry.io);
  }

  get hasMattingModel(): boolean {
    return this.#matting !== null;
  }

  get hasUpscaleModel(): boolean {
    return this.#upscaleStage !== null;
  }

  /**
   * One network pass, then a refine. Also estimates the background colour used
   * by decontamination, which needs a matte to know which pixels are background.
   */
  async runMatting(): Promise<void> {
    if (!this.#source) throw new Error('[pipeline] no image loaded');
    if (!this.#matting) throw new Error('[pipeline] no matting model loaded');

    this.#rawMatte?.release();
    this.#rawMatte = await this.#matting.computeMatte(this.#source, this.matteSettings.normalize);

    if (this.matteSettings.background === null) {
      this.#estimatedBackground = await estimateBackground(this.ctx, this.#source, this.#rawMatte);
    }
    this.refine();
  }

  #estimatedBackground: [number, number, number] = [1, 1, 1];

  /** The colour decontamination is currently correcting against. */
  get effectiveBackground(): [number, number, number] {
    return this.matteSettings.background ?? this.#estimatedBackground;
  }

  /**
   * Re-runs post-processing. Cheap enough for continuous slider drags.
   *
   * Invalidates any upscale, since it was computed from the previous cut-out.
   */
  refine(): void {
    if (!this.#source || !this.#rawMatte) return;

    this.#cutout ??= GpuImage.create(
      this.ctx.device,
      this.#source.width,
      this.#source.height,
      'rgba16float',
      'cutout',
    );

    refineMatte(
      this.ctx,
      this.#source,
      this.#rawMatte,
      this.#paint,
      { ...this.matteSettings, background: this.effectiveBackground },
      this.#cutout,
    );
    this.ctx.flush();

    if (this.#upscaled) {
      this.#upscaled.release();
      this.#upscaled = null;
    }
  }

  /** Applies a brush stroke in source-image pixel coordinates. */
  paint(points: readonly StrokePoint[], target: PaintTarget, brush: BrushOptions): void {
    if (!this.#paint) return;
    paintStroke(this.ctx, this.#paint, points, target, brush);
    this.refine();
  }

  /** Discards every manual correction. */
  clearPaint(): void {
    if (!this.#paint) return;
    clearPaintMap(this.ctx, this.#paint);
    this.refine();
  }

  /** Runs tiled super-resolution over the current cut-out. */
  async runUpscale(
    onProgress?: (progress: UpscaleProgress) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!this.#cutout) throw new Error('[pipeline] run matting before upscaling');
    if (!this.#upscaleStage) throw new Error('[pipeline] no upscale model loaded');

    const result = await this.#upscaleStage.run(
      this.#cutout,
      this.upscaleSettings,
      onProgress,
      signal,
    );
    this.#upscaled?.release();
    this.#upscaled = result;
  }

  /** Encodes the newest artefact as a print-ready file. */
  async export(options: ExportOptions = {}): Promise<ExportResult> {
    const image = this.displayImage;
    if (!image) throw new Error('[pipeline] nothing to export');
    return exportArtwork(this.ctx, image, options);
  }

  /**
   * Packages the newest artefact for a redesign backend: the cut-out plus its
   * matte as a separate greyscale image.
   */
  async exportForRedesign(
    options: { trim?: boolean; trimPadding?: number; maxEdge?: number } = {},
  ): Promise<RedesignAssets> {
    const image = this.displayImage;
    if (!image) throw new Error('[pipeline] nothing to hand off');
    return exportForRedesign(this.ctx, image, options);
  }

  #disposeDocument(): void {
    this.#source?.destroy();
    this.#rawMatte?.release();
    this.#paint?.destroy();
    this.#cutout?.destroy();
    this.#upscaled?.release();
    this.#source = null;
    this.#rawMatte = null;
    this.#paint = null;
    this.#cutout = null;
    this.#upscaled = null;
  }

  async dispose(): Promise<void> {
    this.#disposeDocument();
    this.#matting?.dispose();
    this.#upscaleStage?.dispose();
    await this.#mattingModel?.dispose();
    await this.#upscaleModel?.dispose();
    this.#matting = null;
    this.#upscaleStage = null;
    this.#mattingModel = null;
    this.#upscaleModel = null;
  }
}
